import * as React from 'react'
import * as PropTypes from 'prop-types'
import { css, Rule } from 'glamor'

import { UiTheme, useTheme } from '../../theme'

import { isKey } from '../../utils'

import { Flex } from '../flex'
import { Touchable } from '../touchable'

import Arrow from './arrow.svg'

const getStyles = (theme: UiTheme) => ({
  stepper: {
    base: {
      position: 'relative',
    },

    small: {
      height: '40px',
    },
    medium: {
      height: '60px',
    },
    large: {
      height: '80px',
    },
  },

  step: {
    base: {
      color: '#7A91A8',
      cursor: 'pointer',
      opacity: 1,
    },

    active: {
      color: theme.colors.accents.primary,
    },

    disabled: {
      opacity: 0.6,
      cursor: 'default',
    },
  },

  badge: {
    base: {
      display: 'block',
      marginRight: '10px',
      width: '30px',
      height: '30px',
      borderRadius: '15px',
      lineHeight: '30px',
      color: 'white',
      fontStyle: 'normal',
      textAlign: 'center',
      backgroundColor: theme.colors.accents.secondary,
    },

    active: {
      backgroundColor: theme.colors.accents.primary,
    },
  },

  arrow: {
    height: '100%',
    marginLeft: theme.settings.spacing.medium,
    marginRight: theme.settings.spacing.large,
  },
})

function MaybeTouchable(props: { children: React.ReactNode; onClick?: (e: React.SyntheticEvent) => void }) {
  const { onClick, children } = props

  return onClick ? <Touchable onClick={onClick}>{children}</Touchable> : <>{children}</>
}

interface IStepperProps {
  steps: {
    title: React.ReactNode
    disabled?: boolean
  }[]
  size?: 'large' | 'medium' | 'small'
  activeStep?: number
  onStepChange?: (index: number) => void
  style?: Rule
}

export const Stepper = ({ steps, size, activeStep, onStepChange, style }: IStepperProps) => {
  const styles = getStyles(useTheme())

  return (
    <Flex
      alignItems="center"
      justifyContent="space-evenly"
      row
      style={[styles.stepper.base, isKey(styles.stepper, size) && styles.stepper[size], style]}
    >
      {steps.map(({ title, disabled }, i) => {
        return (
          // eslint-disable-next-line react/no-array-index-key
          <React.Fragment key={i}>
            <MaybeTouchable onClick={disabled ? undefined : () => onStepChange && onStepChange(i)}>
              <Flex
                alignItems="center"
                row
                style={{
                  ...styles.step.base,
                  ...(activeStep === i && styles.step.active),
                  ...(disabled && styles.step.disabled),
                }}
              >
                <i {...css(styles.badge.base, activeStep === i && styles.badge.active)}>{i + 1}</i>
                {title}
              </Flex>
            </MaybeTouchable>
            {i < steps.length - 1 && <Arrow {...css(styles.arrow)} />}
          </React.Fragment>
        )
      })}
    </Flex>
  )
}

Stepper.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.node.isRequired,
      disabled: PropTypes.bool,
    })
  ).isRequired,
  size: PropTypes.oneOf(['large', 'medium', 'small']),
  activeStep: PropTypes.number,
  onStepChange: PropTypes.func,
  style: PropTypes.object,
}

Stepper.defaultProps = {
  size: 'large',
}
